import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  AlertTriangle,
  ArrowLeft,
  Hourglass,
  Rocket,
  Sparkles,
  TrendingUp,
  Trophy,
} from "lucide-react";
import { Panel } from "@/components/game/Panel";
import { PortraitFrame } from "@/components/game/PortraitFrame";
import { StatBar } from "@/components/game/StatBar";
import { Pill, TypeBadge } from "@/components/game/Badges";
import { GoldAmount, MicroLabel, PageHeader, ScreenContainer } from "@/components/game/ScreenKit";
import { InfoTip } from "@/components/game/InfoTip";
import { RouteError, RouteNotFound } from "@/components/game/RouteError";
import { PageSkeleton } from "@/components/game/PageSkeleton";
import { cn } from "@/lib/utils";
import {
  startExpedition,
  claimExpedition,
  cancelExpedition,
  getActiveExpedition,
  listRecentExpeditions,
  type ExpeditionResult,
  type ExpeditionRow,
} from "@/lib/game/expedition.functions";
import { getMyBattleMonster } from "@/lib/game/battle-v2/loaders.functions";
import type { CoreType } from "@/lib/game/combat-core/types";

export const Route = createFileRoute("/_app/game/expeditions")({
  component: ExpeditionsPage,
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
});

type Zone = {
  id: number;
  name_de: string;
  name_en: string;
  lead_type: CoreType;
  base_level: number;
};

const TYPE_STRONG: Record<CoreType, CoreType> = {
  neon: "void",
  void: "plasma",
  plasma: "data",
  data: "glitch",
  glitch: "neon",
};

/**
 * Die drei Laufzeiten. `fights` ist KEINE Schätzung, sondern der Wert, den der
 * Server beim Abholen rechnet: `min(60, floor(duration_minutes / 6))`
 * (`claimExpedition` in `expedition.functions.ts`). 480 Minuten ergäben 80
 * Kämpfe, werden aber auf 60 gedeckelt — und ab Kampf 41 zählt nur die Hälfte
 * (`DIMINISH_AFTER`), deshalb der Hinweis nur dort.
 */
const DURATIONS = [
  { minutes: 30, labelKey: "exp.dur30", fights: 5 },
  { minutes: 120, labelKey: "exp.dur120", fights: 20 },
  { minutes: 480, labelKey: "exp.dur480", fights: 60 },
] as const;

function fmtCountdown(ms: number): string {
  if (ms <= 0) return "00:00";
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  if (h > 0) return `${pad(h)}:${pad(m)}:${pad(sec)}`;
  return `${pad(m)}:${pad(sec)}`;
}

/**
 * Auswahl-Kachel im Stil „Comic Arena": Ink-Outline, harter Offset-Schatten,
 * beim Drücken sackt sie auf den Schatten ab.
 *
 * Ausgewählt wird mit `accent` (Art-Blau) gefüllt, NICHT mit `primary`:
 * Orange ist laut Handoff die einzige Aktionsfarbe und gehört auf dieser Seite
 * dem einen Knopf, der die Expedition wirklich startet bzw. abholt.
 */
function OptionTile({
  selected,
  onClick,
  className,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        "min-h-11 rounded-md border-2 border-ink p-3 text-left shadow-hard-sm",
        "cursor-pointer transition-[transform,box-shadow,background-color] duration-100",
        "hover:-translate-y-px hover:brightness-[1.06] active:translate-y-1 active:shadow-none",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        selected ? "bg-accent text-accent-foreground" : "bg-secondary text-foreground",
        className,
      )}
    >
      {children}
    </button>
  );
}

/** Beschriftetes Wertepaar in der Kopfzeile der laufenden Expedition. */
function FactCell({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <MicroLabel>{label}</MicroLabel>
      <div className="mt-0.5 text-[13px] font-extrabold tabular-nums [overflow-wrap:anywhere]">
        {children}
      </div>
    </div>
  );
}

function ExpeditionsPage() {
  const { t, i18n } = useTranslation("game");
  const qc = useQueryClient();
  const lang = i18n.language.startsWith("de") ? "de" : "en";

  const fetchActive = useServerFn(getActiveExpedition);
  const fetchRecent = useServerFn(listRecentExpeditions);
  const fetchMon = useServerFn(getMyBattleMonster);
  const start = useServerFn(startExpedition);
  const claim = useServerFn(claimExpedition);
  const cancel = useServerFn(cancelExpedition);

  const activeQ = useQuery({
    queryKey: ["expedition-active"],
    queryFn: () => fetchActive(),
    refetchInterval: 15_000,
  });
  const recentQ = useQuery({
    queryKey: ["expedition-recent"],
    queryFn: () => fetchRecent(),
  });
  const monQ = useQuery({
    queryKey: ["battle-monster"],
    queryFn: () => fetchMon(),
    staleTime: 30_000,
  });

  const zonesQ = useQuery({
    queryKey: ["exp-zones"],
    queryFn: async () => {
      const [z, p] = await Promise.all([
        supabase.from("zones").select("id, name_de, name_en, lead_type, base_level").order("id"),
        supabase.from("player_zone_progress").select("highest_zone_unlocked").maybeSingle(),
      ]);
      if (z.error) throw z.error;
      const highest = p.data?.highest_zone_unlocked ?? 1;
      return {
        zones: (z.data ?? []).filter((row) => row.id <= highest) as Zone[],
        highest,
      };
    },
  });

  const [zoneId, setZoneId] = useState<number | null>(null);
  const [risk, setRisk] = useState<"sicher" | "riskant">("sicher");
  const [duration, setDuration] = useState<30 | 120 | 480>(30);
  const [showReport, setShowReport] = useState<ExpeditionResult | null>(null);

  useEffect(() => {
    if (!zoneId && zonesQ.data?.zones?.length) setZoneId(zonesQ.data.zones[0].id);
  }, [zonesQ.data, zoneId]);

  // Countdown-Ticker
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const iv = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(iv);
  }, []);

  const startMut = useMutation({
    mutationFn: (data: {
      zoneId: number;
      risk: "sicher" | "riskant";
      durationMinutes: 30 | 120 | 480;
    }) => start({ data }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["expedition-active"] });
      toast.success(t("exp.toast.started"));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const claimMut = useMutation({
    mutationFn: (id: string) => claim({ data: { expeditionId: id } }),
    onSuccess: (r) => {
      setShowReport(r.result);
      qc.invalidateQueries({ queryKey: ["expedition-active"] });
      qc.invalidateQueries({ queryKey: ["expedition-recent"] });
      qc.invalidateQueries({ queryKey: ["my-monster"] });
      qc.invalidateQueries({ queryKey: ["battle-monster"] });
      // Eine Expedition kann leveln, Attacken lernen und ein Lern-Angebot
      // hinterlassen. Ohne diese beiden Keys kämpft die nächste Stage mit dem
      // alten Snapshot, und das Angebot taucht erst nach einem Reload auf.
      qc.invalidateQueries({ queryKey: ["battle-v2", "monster"] });
      qc.invalidateQueries({ queryKey: ["monster-moves"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const cancelMut = useMutation({
    mutationFn: (id: string) => cancel({ data: { expeditionId: id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["expedition-active"] });
      toast.success(t("exp.toast.cancelled"));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (activeQ.isLoading || zonesQ.isLoading) return <PageSkeleton />;

  const active: ExpeditionRow | null = activeQ.data ?? null;
  const monsterType = (monQ.data?.type ?? "neon") as CoreType;
  const zones = zonesQ.data?.zones ?? [];
  const selectedZone = zones.find((z) => z.id === zoneId) ?? null;
  const selectedSynergy = selectedZone && TYPE_STRONG[monsterType] === selectedZone.lead_type;
  const endsAtMs = active ? new Date(active.ends_at).getTime() : 0;
  const claimable = active && endsAtMs <= now;
  const remainMs = active ? Math.max(0, endsAtMs - now) : 0;

  const zoneName = (id: number) => {
    const z = zones.find((zz) => zz.id === id);
    return z ? (lang === "de" ? z.name_de : z.name_en) : `#${id}`;
  };
  const durationLabel = (minutes: number) => {
    const d = DURATIONS.find((x) => x.minutes === minutes);
    return d ? t(d.labelKey) : `${minutes} min`;
  };
  const fightsFor = (minutes: number) =>
    DURATIONS.find((x) => x.minutes === minutes)?.fights ?? null;
  // Als `const`, damit die Prüfung unten den Typ auf `number` einengt —
  // `t(..., { count })` nimmt kein `null`.
  const activeFights = active ? fightsFor(active.duration_minutes) : null;

  // Unterwegs ist das Monster vom START der Expedition (`expeditions.monster_id`).
  // Nur wenn das noch dasselbe ist, das gerade im Stall aktiv ist, dürfen wir
  // sein Bild und seinen Namen zeigen — sonst behaupteten wir, ein anderes
  // Xenomon sei draußen, als es tatsächlich ist.
  const activeMonsterIsOnTour = !!active && monQ.data?.id === active.monster_id;

  return (
    <ScreenContainer className="space-y-4">
      <PageHeader
        title={t("exp.title")}
        icon={<Rocket className="h-[22px] w-[22px] shrink-0 text-primary" strokeWidth={2.5} />}
        sub={t("exp.intro")}
      />

      {/* Bericht nach dem Abholen */}
      {showReport && <ReportCard result={showReport} onClose={() => setShowReport(null)} t={t} />}

      {/* Aktive Expedition */}
      {active ? (
        <Panel tone="primary" grid contentClassName="p-3.5">
          <div className="flex flex-wrap items-center gap-3.5">
            <PortraitFrame
              src={activeMonsterIsOnTour ? monQ.data?.imageUrl : null}
              alt={activeMonsterIsOnTour ? (monQ.data?.displayName ?? "") : ""}
              size={72}
              ringColor={activeMonsterIsOnTour ? `var(--type-${monsterType})` : undefined}
              float
            />

            <div className="min-w-[220px] flex-1">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <h3 className="flex min-w-0 items-center gap-2 text-lg leading-tight">
                  <Hourglass className="h-4 w-4 shrink-0" strokeWidth={2.5} />
                  <span className="[overflow-wrap:anywhere]">
                    {t("exp.active.title")}
                    {activeMonsterIsOnTour && monQ.data?.displayName
                      ? ` · ${monQ.data.displayName}`
                      : ""}
                  </span>
                </h3>
                <span className="font-display text-xl leading-none tabular-nums">
                  {fmtCountdown(remainMs)}
                </span>
              </div>

              <div className="mt-2.5 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
                <FactCell label={t("exp.zone")}>{zoneName(active.zone_id)}</FactCell>
                <FactCell label={t("exp.riskLabel")}>
                  <InfoTip text={t(`exp.risk.${active.risk}Desc`)}>
                    <Pill
                      style={{
                        background:
                          active.risk === "riskant" ? "var(--destructive)" : "var(--secondary)",
                        color: "var(--foreground)",
                      }}
                    >
                      {t(`exp.risk.${active.risk}`)}
                    </Pill>
                  </InfoTip>
                </FactCell>
                <FactCell label={t("exp.duration")}>
                  {activeFights !== null ? (
                    <InfoTip text={t("exp.approxFights", { count: activeFights })}>
                      <span className="underline decoration-dotted underline-offset-2">
                        {durationLabel(active.duration_minutes)}
                      </span>
                    </InfoTip>
                  ) : (
                    durationLabel(active.duration_minutes)
                  )}
                </FactCell>
              </div>

              {/* Der Balken zeigt die verbleibende Zeit in Minuten; die exakte
                  Restzeit steht als Countdown oben rechts. */}
              <StatBar
                className="mt-3"
                label={t("exp.remaining")}
                value={Math.ceil(remainMs / 60_000)}
                max={active.duration_minutes}
                color="energy"
              />
            </div>

            <div className="flex w-full flex-col gap-2 sm:w-auto">
              {claimable ? (
                <Button onClick={() => claimMut.mutate(active.id)} disabled={claimMut.isPending}>
                  <Trophy strokeWidth={2.5} />
                  {t("exp.claim")}
                </Button>
              ) : (
                <Button disabled variant="secondary">
                  {t("exp.notReady")}
                </Button>
              )}
              {!claimable && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    if (window.confirm(t("exp.cancelConfirm"))) cancelMut.mutate(active.id);
                  }}
                  disabled={cancelMut.isPending}
                >
                  {t("exp.cancel")}
                </Button>
              )}
            </div>
          </div>
        </Panel>
      ) : (
        /* Neue Expedition */
        <Panel contentClassName="p-3.5">
          <h3 className="text-lg leading-tight">{t("exp.start.title")}</h3>

          <div className="mt-3 space-y-3.5">
            {/* Zonenwahl */}
            <div>
              <MicroLabel className="mb-1.5">{t("exp.zone")}</MicroLabel>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {zones.map((z) => {
                  const syn = TYPE_STRONG[monsterType] === z.lead_type;
                  const isSel = zoneId === z.id;
                  return (
                    <OptionTile key={z.id} selected={isSel} onClick={() => setZoneId(z.id)}>
                      <div className="flex items-start justify-between gap-2">
                        <span className="min-w-0 font-display text-sm leading-tight [overflow-wrap:anywhere]">
                          {lang === "de" ? z.name_de : z.name_en}
                        </span>
                        <span className="shrink-0 text-[11px] font-extrabold tabular-nums opacity-80">
                          Lv {z.base_level}
                        </span>
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        <TypeBadge type={z.lead_type} />
                        {syn && (
                          <span
                            className="inline-flex items-center gap-1 text-[11px] font-extrabold leading-tight [overflow-wrap:anywhere]"
                            style={{ color: "var(--hp-ally)" }}
                          >
                            <TrendingUp className="h-3 w-3 shrink-0" strokeWidth={2.5} />
                            {t("exp.synergyHint")}
                          </span>
                        )}
                      </div>
                    </OptionTile>
                  );
                })}
              </div>
            </div>

            {/* Risiko */}
            <div>
              <MicroLabel className="mb-1.5">{t("exp.riskLabel")}</MicroLabel>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {(["sicher", "riskant"] as const).map((r) => (
                  <OptionTile key={r} selected={risk === r} onClick={() => setRisk(r)}>
                    <div className="font-display text-base leading-tight">{t(`exp.risk.${r}`)}</div>
                    <div
                      className={cn(
                        "mt-1 text-[11px] font-extrabold leading-snug",
                        risk === r ? "text-accent-foreground/80" : "text-muted-foreground",
                      )}
                    >
                      {t(`exp.risk.${r}Desc`)}
                    </div>
                  </OptionTile>
                ))}
              </div>
            </div>

            {/* Dauer */}
            <div>
              <MicroLabel className="mb-1.5">{t("exp.duration")}</MicroLabel>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                {DURATIONS.map((d) => (
                  <OptionTile
                    key={d.minutes}
                    selected={duration === d.minutes}
                    onClick={() => setDuration(d.minutes)}
                  >
                    <div className="font-display text-lg leading-tight">{t(d.labelKey)}</div>
                    <div
                      className={cn(
                        "mt-0.5 text-[11px] font-extrabold leading-snug tabular-nums",
                        duration === d.minutes
                          ? "text-accent-foreground/80"
                          : "text-muted-foreground",
                      )}
                    >
                      {t("exp.approxFights", { count: d.fights })}
                      {d.minutes === 480 && ` · ${t("exp.diminishHint")}`}
                    </div>
                  </OptionTile>
                ))}
              </div>
            </div>

            {selectedSynergy && (
              <div
                className="flex items-center gap-2 rounded-md border-2 border-ink bg-secondary px-3 py-2 text-xs font-extrabold shadow-hard-sm"
                style={{ color: "var(--hp-ally)" }}
              >
                <TrendingUp className="h-4 w-4 shrink-0" strokeWidth={2.5} />
                <span className="[overflow-wrap:anywhere]">{t("exp.synergyBanner")}</span>
              </div>
            )}

            <Button
              size="lg"
              onClick={() => zoneId && startMut.mutate({ zoneId, risk, durationMinutes: duration })}
              disabled={!zoneId || startMut.isPending || !monQ.data}
              className="w-full sm:w-auto"
            >
              <Rocket strokeWidth={2.5} />
              {t("exp.start.button")}
            </Button>
          </div>
        </Panel>
      )}

      {/* Letzte Expeditionen */}
      {recentQ.data && recentQ.data.length > 0 && (
        <div>
          <h3 className="mb-2 text-base leading-tight">{t("exp.recent.title")}</h3>
          <div className="space-y-2">
            {recentQ.data.map((e) => {
              const r = e.result as ExpeditionResult | null;
              return (
                <div
                  key={e.id}
                  className="rounded-md border-2 border-ink bg-card px-3 py-2 shadow-hard-sm"
                >
                  <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="text-[13px] font-extrabold [overflow-wrap:anywhere]">
                          {zoneName(e.zone_id)}
                        </span>
                        <span className="text-[11px] font-extrabold tabular-nums text-muted-foreground">
                          {durationLabel(e.duration_minutes)}
                        </span>
                        <Pill
                          style={{
                            background:
                              e.risk === "riskant" ? "var(--destructive)" : "var(--secondary)",
                            color: "var(--foreground)",
                          }}
                        >
                          {t(`exp.risk.${e.risk}`)}
                        </Pill>
                      </div>
                      <div className="mt-0.5 text-[11px] font-extrabold tabular-nums text-muted-foreground">
                        {new Date(e.claimed_at ?? e.ends_at).toLocaleString()}
                      </div>
                    </div>
                    {r && (
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-extrabold tabular-nums text-muted-foreground">
                        <span>
                          {r.xp} {t("exp.report.xp")}
                        </span>
                        <GoldAmount value={r.gold} />
                        <span>
                          {r.itemsCreated} {t("exp.report.items")}
                        </span>
                      </div>
                    )}
                  </div>
                  {r?.abortedAtStage != null && (
                    <div className="mt-1.5 flex items-start gap-1.5 text-[11px] font-extrabold leading-snug text-destructive">
                      <AlertTriangle className="mt-px h-3 w-3 shrink-0" strokeWidth={2.5} />
                      <span>{t("exp.report.aborted", { stage: r.abortedAtStage })}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="pt-1">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/game/battle">
            <ArrowLeft strokeWidth={2.5} />
            {t("exp.backToBattle")}
          </Link>
        </Button>
      </div>
    </ScreenContainer>
  );
}

/** Wert-Kachel im Bericht — dieselbe Ink-Kachel wie auf der Sammelkarte. */
function StatTile({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="rounded-md bg-ink px-2 py-1.5 text-center">
      <div className="flex min-h-[26px] items-center justify-center font-display text-lg font-normal leading-none tabular-nums text-primary">
        {children}
      </div>
      <span className="mt-0.5 block text-[9px] uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </span>
    </div>
  );
}

/** Zeile in den Höhepunkten: Mikro-Label links, Zahl rechts. */
function HighlightRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-t-2 border-ink/40 pt-1.5 first:border-t-0 first:pt-0">
      <MicroLabel className="[overflow-wrap:anywhere]">{label}</MicroLabel>
      <span className="shrink-0 text-[13px] font-extrabold tabular-nums">{value}</span>
    </div>
  );
}

function ReportCard({
  result,
  onClose,
  t,
}: {
  result: ExpeditionResult;
  onClose: () => void;
  t: (k: string, o?: Record<string, unknown>) => string;
}) {
  return (
    <Panel tone="primary" grid contentClassName="p-3.5">
      <h3 className="flex items-center gap-2 text-lg leading-tight">
        <Trophy className="h-5 w-5 shrink-0 text-primary" strokeWidth={2.5} />
        {t("exp.report.title")}
      </h3>

      <div className="mt-3 grid grid-cols-3 gap-1.5 sm:grid-cols-6">
        <StatTile label={t("exp.report.xp")}>{result.xp}</StatTile>
        <StatTile label={t("exp.report.gold")}>
          <GoldAmount value={result.gold} size="lg" className="font-display font-normal" />
        </StatTile>
        <StatTile label={t("exp.report.items")}>{result.itemsCreated}</StatTile>
        <StatTile label={t("exp.report.stages")}>{result.stagesCleared}</StatTile>
        <StatTile label={t("exp.report.wins")}>{result.kills}</StatTile>
        <StatTile label={t("exp.report.defeats")}>{result.defeats}</StatTile>
      </div>

      <div className="mt-3 space-y-1.5 rounded-md border-2 border-ink bg-secondary px-3 py-2">
        <HighlightRow label={t("exp.report.bestCrit")} value={result.highlights.bestCrit} />
        <HighlightRow label={t("exp.report.maxCombo")} value={result.highlights.maxCombo} />
        <HighlightRow
          label={t("exp.report.longestFight")}
          value={`${Math.round(result.highlights.longestFight / 100) / 10}s`}
        />
      </div>

      {/* Level-Ups aus dem Claim. `result.levelUp` wurde bisher zwar
          gefüllt und gespeichert, aber von keinem Client gelesen —
          Expeditionen levelten und lernten stumm. Altzeilen tragen das Feld
          nicht (`undefined`), deshalb der Optional-Zugriff. */}
      {result.levelUp && (
        <div className="mt-3 rounded-md border-2 border-accent bg-card px-3 py-2.5 shadow-hard-sm">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 shrink-0 text-primary" strokeWidth={2.5} />
            <span className="font-display text-base leading-tight text-primary">
              {t("exp.report.levelUp", { level: result.levelUp.toLevel })}
            </span>
          </div>
          <div className="mt-1.5 space-y-1 text-xs font-extrabold leading-snug">
            <div className="tabular-nums text-muted-foreground">
              {t("exp.report.levelUpGains", {
                atk: result.levelUp.gains.atk,
                def: result.levelUp.gains.def,
                hp: result.levelUp.gains.hp_max,
              })}
            </div>
            {result.levelUp.newMoves.length > 0 && (
              <div className="[overflow-wrap:anywhere]">
                {t("exp.report.levelUpMoves", {
                  moves: result.levelUp.newMoves
                    .map((m) => `${m.name} (Slot ${m.slot})`)
                    .join(", "),
                })}
              </div>
            )}
            {result.levelUp.moveOfferOpen && (
              <div className="[overflow-wrap:anywhere] text-foreground">
                {t("exp.report.levelUpOffer")}
              </div>
            )}
            {(result.levelUp.moveOffersSkipped ?? 0) > 0 && (
              <div className="[overflow-wrap:anywhere] text-muted-foreground">
                {t("exp.report.levelUpSkipped", { count: result.levelUp.moveOffersSkipped })}
              </div>
            )}
          </div>
        </div>
      )}

      {result.typeSynergyBonus && (
        <div
          className="mt-3 flex items-center gap-2 rounded-md border-2 border-ink bg-secondary px-3 py-2 text-xs font-extrabold"
          style={{ color: "var(--hp-ally)" }}
        >
          <TrendingUp className="h-4 w-4 shrink-0" strokeWidth={2.5} />
          <span className="[overflow-wrap:anywhere]">{t("exp.report.synergy")}</span>
        </div>
      )}

      {result.abortedAtStage != null && (
        <div className="mt-3 flex items-start gap-2 rounded-md border-2 border-destructive bg-card px-3 py-2 text-xs font-extrabold leading-snug text-destructive">
          <AlertTriangle className="mt-px h-4 w-4 shrink-0" strokeWidth={2.5} />
          <span className="[overflow-wrap:anywhere]">
            {t("exp.report.aborted", { stage: result.abortedAtStage })}
          </span>
        </div>
      )}

      <Button variant="outline" size="sm" className="mt-3" onClick={onClose}>
        {t("exp.report.close")}
      </Button>
    </Panel>
  );
}
